/**
 * Map of restricted country ISO codes to their full names.
 * Users from these countries are not eligible for certain services (e.g., Socure verification).
 * Includes special cases for display purposes (e.g., "Myanmar (Burma)", "Hong Kong (China)").
 */
export const RESTRICTED_COUNTRIES_MAP: Record<string, string> = {
    TH: 'Thailand',
    TW: 'Taiwan',
    UA: 'Ukraine',
    CU: 'Cuba',
    HU: 'Hungary',
    IR: 'Iran',
    KP: 'North Korea',
    RU: 'Russia',
    BY: 'Belarus',
    SY: 'Syria',
    VE: 'Venezuela',
    MM: 'Myanmar (Burma)',
    SD: 'Sudan',
    SS: 'South Sudan',
    SO: 'Somalia',
    LY: 'Libya',
    IQ: 'Iraq',
    AF: 'Afghanistan',
    YE: 'Yemen',
    LB: 'Lebanon',
    CF: 'Central African Republic',
    CD: 'Democratic Republic of the Congo',
    ET: 'Ethiopia',
    NI: 'Nicaragua',
    ML: 'Mali',
    NE: 'Niger',
    HT: 'Haiti',
    ZW: 'Zimbabwe',
    DZ: 'Algeria',
    AO: 'Angola',
    BO: 'Bolivia',
    BG: 'Bulgaria',
    BF: 'Burkina Faso',
    CM: 'Cameroon',
    CN: 'China',
    CI: "Côte d'Ivoire",
    KE: 'Kenya',
    LA: 'Laos',
    MC: 'Monaco',
    MZ: 'Mozambique',
    NA: 'Namibia',
    AE: 'United Arab Emirates',
    IN: 'India',
};

/**
 * List of restricted country ISO codes.
 * Derived from RESTRICTED_COUNTRIES_MAP keys.
 */
export const RESTRICTED_COUNTRY_CODES = Object.keys(RESTRICTED_COUNTRIES_MAP);

/**
 * List of restricted country names for display purposes.
 * Derived from RESTRICTED_COUNTRIES_MAP values.
 */
export const RESTRICTED_COUNTRY_NAMES = Object.values(RESTRICTED_COUNTRIES_MAP);

export const REFERRALS_ALLOWLISTED_COUNTRIES_MAP: Record<string, string> = {
    US: 'United States',
    PR: 'Puerto Rico',
    AS: 'American Samoa',
    GU: 'Guam',
    MP: 'Northern Mariana Islands',
    UM: 'United States Minor Outlying Islands',
    VI: 'U.S. Virgin Islands',
};

export const REFERRALS_ALLOWLISTED_COUNTRY_CODES = Object.keys(REFERRALS_ALLOWLISTED_COUNTRIES_MAP);
export const REFERRALS_ALLOWLISTED_COUNTRY_NAMES = Object.values(REFERRALS_ALLOWLISTED_COUNTRIES_MAP);

/**
 * Whether signup collects a national ID for the given country.
 * All non-domestic countries collect one; the US and its territories collect SSN instead.
 * The backend decides per-country whether the national ID is forwarded to Socure.
 */
export const requiresNationalId = (country: string): boolean =>
    !REFERRALS_ALLOWLISTED_COUNTRY_CODES.includes(country.trim().toUpperCase() || 'US');

/**
 * Per-country name and format of the national ID collected at signup.
 * The KYC vendor input-validates the value against the country's own format, so a
 * generic "National ID" prompt leaves users guessing and retrying blind. Countries
 * absent from this map fall back to the generic label with no format hint.
 */
export const NATIONAL_ID_FORMATS: Record<string, { label: string; hint?: string }> = {
    AR: { label: 'DNI', hint: '7 or 8 digits' },
    AU: { label: 'Tax File Number (TFN)', hint: '9 digits' },
    BD: { label: 'National ID (NID)', hint: '13 digits' },
    BR: { label: 'CPF', hint: '11 digits' },
    CA: { label: 'Social Insurance Number (SIN)', hint: '9 digits' },
    CL: { label: 'RUT' },
    CO: { label: 'Cédula de Ciudadanía' },
    DK: { label: 'CPR number', hint: '10 digits' },
    FI: { label: 'Henkilötunnus', hint: 'DDMMYY-NNNX — keep the century mark' },
    GB: { label: 'National Insurance number' },
    HK: { label: 'HKID number' },
    IE: { label: 'PPS number' },
    IT: { label: 'Codice Fiscale', hint: '16 characters' },
    MX: { label: 'CURP', hint: '18 characters — an RFC is also accepted' },
    MY: { label: 'NRIC number', hint: '12 digits' },
    NG: { label: 'National Identification Number (NIN)', hint: '11 digits' },
    NZ: { label: 'IRD number' },
    PE: { label: 'DNI' },
    PL: { label: 'PESEL', hint: '11 digits' },
    SE: { label: 'Personnummer', hint: '10 digits' },
    SG: { label: 'NRIC or FIN' },
    TR: { label: 'T.C. Kimlik No', hint: '11 digits' },
    UY: { label: 'Cédula de Identidad' },
    ZA: { label: 'ID number', hint: '13 digits' },
};

/**
 * Label and format hint for the national ID field, falling back to a generic
 * prompt for countries whose ID format we do not spell out.
 */
export const getNationalIdFormat = (country: string): { label: string; hint?: string } =>
    NATIONAL_ID_FORMATS[country.trim().toUpperCase()] ?? { label: 'National ID' };
